/* CASH REPORT — THE MONTHLY CASH PLAN (2026-10-25). It replaces the 13 Weeks Cash Plan.

   Jay Sung for the Finance team, 2026-10-21: "Weekly balance is too complicated so better to change to monthly cash
   balance (Plz use the format attached) — Even the format is based on monthly, but this format will be updated by
   weekly — Each entity cash forecast + Group cash forecast need to be shown". The format is his "Group Cash Forecast"
   workbook, and Max asked for it as close to the file as it can be ("format giống nhất theo như file á. do user đã quen
   với format này rùi"). So the screen is the workbook: SHEET TABS — Group CF first, then one per entity in the
   workbook's order — and on each tab what that sheet shows.

   GROUP CF, as the workbook lays it out: Beginning cash, Net increase (decrease) in cash, – Operation, – Finance &
   Investment, Ending cash, each with every entity's line and the Total under it, in thousands (K SGD — or K USD / K KRW,
   the reader's pick) at ONE rate per currency, NetSuite's at the last month-end before the first month (Max: "tỷ giá
   NetSuite cuối tháng gần nhất"), and the rates printed under it as the workbook does. An entity tab is its own sheet in
   its own currency (CashPlanSheet.jsx). Only AGREED plans count (the Reviewer's agreement, as everywhere in the Cash
   Report): a week whose plan is still with the Reviewer reads the last agreed one, and says which. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { API, call, fmtDay, StateChip, LastStamp, PeriodBar, TestAsBar, WaitingTag, BasePick, readBase, saveBase, toBase } from './cashUi';
import { MonthPlanCharts } from './CashCharts';
import { SheetTable, SheetTitle, monthLabel } from './CashPlanSheet';
import './SPHub.css';
import './ProjectApproval.css';
import './CashSetup.css';
import './CashWeekly.css';

export { monthLabel };
const MEASURES = [['begin', 'r_begin'], ['net', 'r_net'], ['op', 'r_opFull'], ['fi', 'r_fiFull'], ['end', 'r_end']];

/* A Group CF figure: thousands, whole, red brackets below nought, a dash for nought — the workbook's own look. */
function K({ v, strong }) {
  if (v === null || v === undefined || Number.isNaN(v)) return <span className="ps-zero">-</span>;
  const x = Math.round(v / 1000);
  if (x === 0) return <span className="ps-zero">-</span>;
  const s = Math.abs(x).toLocaleString('en-US');
  const txt = x < 0 ? `(${s})` : s;
  return <span className={x < 0 ? 'ps-neg' : ''}>{strong ? <b>{txt}</b> : txt}</span>;
}

function GroupCF({ t, data, months, base, conv }) {
  const ents = data.entities;
  return (
    <div className="cs-table-wrap ps-wrap" data-tour="mp-group">
      <table className="ps-table mp-gcf">
        <thead>
          <tr className="ps-months">
            <th className="ps-lab">{`(K ${base})`}</th>
            {months.map((m) => <th key={m} className="num">{monthLabel(m)}</th>)}
          </tr>
        </thead>
        <tbody>
          {MEASURES.map(([key, label]) => (
            <React.Fragment key={key}>
              <tr className="ps-row ps-head ps-bold" data-measure={key}><td colSpan={months.length + 1}>{t(`xCashMp.${label}`)}</td></tr>
              {ents.map((e) => (
                <tr key={`${key}-${e.entity}`} className={`ps-row${e.sgd ? '' : ' mp-noplan'}`} data-gcf={`${key}|${e.entity}`}>
                  <td className="ps-lab mp-ent" title={e.sgd ? `${e.name} · ${e.currency}` : `${e.name} — ${t('xCashMp.noPlan')}`}>{e.entity}</td>
                  {months.map((m, j) => <td key={m} className="num"><K v={e.sgd ? conv(e.sgd[j][key]) : null} /></td>)}
                </tr>
              ))}
              <tr className="ps-row ps-bold ps-total" data-gcf={`${key}|Total`}>
                <td className="ps-lab">{t('xCashMp.total')}</td>
                {months.map((m, j) => <td key={m} className="num"><K v={data.group ? conv(data.group[j][key]) : null} strong /></td>)}
              </tr>
              <tr className="ps-blank"><td colSpan={months.length + 1} /></tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* The rates the group is converted at, printed under it as the workbook does ("as of 30 Sep 2026 · 1 SGD - KRW 1,058"). */
function RateBlock({ t, data }) {
  const ccys = [...new Set(data.entities.map((e) => e.currency))].filter((c) => c !== 'SGD');
  return (
    <div className="mp-rates" data-tour="mp-rates">
      <b>{t('xCashMp.ratesAsOf', { date: fmtDay(data.rateDate) })}</b>
      {ccys.map((c) => {
        const r = Number(data.rates?.[c]);
        return <span key={c} className="mp-rate">{`1 SGD - ${c} `}{r > 0 ? (1 / r).toLocaleString('en-US', { maximumFractionDigits: (1 / r) >= 100 ? 0 : 4 }) : '—'}</span>;
      })}
      {data.rateFallbackWeek && <span className="cw-sub">{t('xCashMp.rateFallback', { week: data.rateFallbackWeek })}</span>}
    </div>
  );
}

/* Where each entity's plan stands this week — the workbook has no such thing; it is what the portal adds. */
function StatusList({ t, data }) {
  return (
    <div className="mp-status">
      {data.entities.map((e) => (
        <div key={e.entity} className="mp-st" data-plan-row={e.entity}>
          <b>{e.entity}</b> <StateChip t={t} row={e} /><WaitingTag t={t} data={data} entity={e.entity} />
          {e.errors > 0 && <span className="cs-tab-err" title={t('cashw.errorsN', { count: e.errors })}>{e.errors}</span>}
          <span className="cw-sub">{e.from ? (e.from === data.period.code ? t('xCashMp.from', { week: e.from }) : t('xCashMp.fromOlder', { week: e.from })) : t('xCashMp.noPlan')}</span>
        </div>
      ))}
    </div>
  );
}

export default function CashPlan() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [base, setBase] = useState(readBase);
  const period = params.get('period') || '';
  const sheet = params.get('sheet') || (params.get('entity') || '').split(',')[0] || 'GROUP';

  const load = useCallback(async () => {
    try {
      setData(await call(`${API}/view/plan${period ? `?period=${encodeURIComponent(period)}` : ''}`)); setError('');
    } catch (e) { setError(e.message); }
  }, [period]);
  useEffect(() => { load(); }, [load]);
  const setQuery = (next) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
    p.delete('entity');
    setParams(p, { replace: true });
  };
  const pickBase = (b) => { setBase(b); saveBase(b); };
  const conv = useMemo(() => (v) => toBase(v, data?.rates, base), [data, base]);

  if (error) return <div className="pa-banner err">{error}</div>;
  if (!data) return <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cash.loading')}</div>;
  const w = data.period;
  const months = data.months || [];
  const ent = data.entities.find((e) => e.entity === sheet) || null;
  const onGroup = !ent;
  const baseMissing = base !== 'SGD' && !(Number(data.rates?.[base]) > 0);
  return (
    <>
      <TestAsBar t={t} me={data.me} />
      <PeriodBar t={t} periods={data.periods} period={w} onPick={(code) => setQuery({ period: code })}
        trail={onGroup ? <BasePick t={t} value={base} onChange={pickBase} /> : null}>
        {t('xCashMp.planLine', { week: w.code, end: fmtDay(w.end), first: monthLabel(months[0]), last: monthLabel(months[months.length - 1]) })}
        <div className="cw-sub">{t('xCashMp.rateLine', { date: fmtDay(data.rateDate) })} · {t('xCashMp.groupCount', { n: data.planned, of: data.entities.length })}</div>
      </PeriodBar>

      {/* THE WORKBOOK'S SHEET TABS: Group CF, then each entity in the workbook's order. */}
      <div className="mp-tabs" role="tablist" data-tour="mp-tabs">
        {[{ entity: 'GROUP', label: 'Group CF' }, ...data.entities.map((e) => ({ entity: e.entity, label: e.entity, e }))].map((x) => {
          const on = x.entity === 'GROUP' ? onGroup : x.entity === sheet;
          return (
            <button key={x.entity} type="button" role="tab" aria-selected={on} className={`mp-tab${on ? ' on' : ''}${x.e && !x.e.sgd ? ' empty' : ''}`}
              title={x.e ? (x.e.sgd ? x.e.name : `${x.e.name} — ${t('xCashMp.noPlan')}`) : t('xCashMp.groupTitle')}
              onClick={() => setQuery({ sheet: x.entity === 'GROUP' ? '' : x.entity })}>{x.label}</button>
          );
        })}
      </div>

      {onGroup ? (
        <>
          <div className="pa-section cw-plan">
            <div className="ps-title">
              <div className="ps-band">Group</div>
              <div className="ps-name">{t('xCashMp.groupHeading')}</div>
            </div>
            {baseMissing && <div className="pa-banner err">{t('xCashMp.rateMissing', { ccy: base })}</div>}
            {!data.group && <div className="pa-banner">{t('xCashMp.groupNone')}</div>}
            <GroupCF t={t} data={data} months={months} base={base} conv={conv} />
            <RateBlock t={t} data={data} />
          </div>
          <div className="pa-section cw-plan" data-tour="mp-charts">
            <MonthPlanCharts t={t} data={data} base={base} conv={conv} />
          </div>
          <div className="pa-section cw-plan">
            <StatusList t={t} data={data} />
            <p className="cs-hint">{t('xCashMp.listHint')}</p>
          </div>
        </>
      ) : (
        <div className="pa-section cw-plan" data-tour="mp-sheet">
          <div className="mp-st mp-st-one" data-plan-row={ent.entity}>
            <StateChip t={t} row={ent} /><WaitingTag t={t} data={data} entity={ent.entity} /><LastStamp t={t} stamps={ent.stamps} />
            <span className="cw-sub">{ent.from ? (ent.from === w.code ? t('xCashMp.from', { week: ent.from }) : t('xCashMp.fromOlder', { week: ent.from })) : ''}</span>
          </div>
          <SheetTitle entity={ent.entity} format={ent.format} />
          {ent.isDefault && <p className="cs-hint">{t('xCashMp.standardFormat', { entity: ent.entity })}</p>}
          {/* With no agreed plan the sheet is still drawn — its rows, empty — so its layout can be read before anybody
              has filled it in. */}
          {!ent.sheet && <div className="pa-banner">{ent.pending ? t('cashw.notReviewed') : t('xCashMp.noPlan')}</div>}
          <SheetTable t={t} format={ent.format} rows={ent.sheet || ent.format.rows} months={months} />
        </div>
      )}
    </>
  );
}
